import React, { Fragment } from "react";
import {
  Checkbox,
  ChoiceGroup,
  ComboBox,
  DateTimeBox,
  DropDown,
  ExpandableContainer,
  Html,
  ICheckbox,
  IComboBox,
  IDateTimeBox,
  IDropDown,
  IFormField,
  IHtml,
  IImagePicker,
  IImportFile,
  ILink,
  IMessageBar,
  IMultiCheckbox,
  IMultiValue,
  IObjectDisplay,
  IText,
  ITextField,
  IToggle,
  ImagePicker,
  ImportFile,
  InlineMultiValue,
  Label,
  Link,
  LoadingOverlay,
  MessageBar,
  MessageBarTypeExtended,
  MultiCheckbox,
  MultiValue,
  MultiValueVariants,
  ObjectDisplay,
  Text,
  TextField,
  Toggle,
  WysiwygHtmlEditor,
  stringUtils,
  IChoiceGroup,
  IUIItem,
} from "cayo.ui";
import { LicenseOptions, ILicenseLink } from "@cayo/admin-components/components/LicenseOptions";
import { AvailabilitySchedule } from "@cayo/shared/api/types/cayo-graph";
import { IFixedList, ITextRun } from "@cayo/shared/api/types/schema";
import TextRun from "@cayo/shared/components/common/TextRun";
import {
  IAdditionalFormFieldProps,
  IAdditionalFormProps,
} from "@cayo/shared/components/Form/types";
import { INode } from "@cayo/shared/components/TreeSelect";
import { commonMessages } from "@cayo/shared/messages/common";
import { bindingsUtils } from "@cayo/shared/scheme/bindings";
import renderForm from ".";
import { IEula, IInplaceAvailabilitySchedule, IObjectLinks, ITab } from "../../../api/schema.api";
import ObjectLink from "../../Basic/ObjectLink";
import { ActivationPlan, AgreementBanner, TrialFeatureList } from "../../Freemium";
import InplaceAvailabilitySchedule from "../../InplaceAvailabilitySchedule";
import TimeLimit from "../../InplaceAvailabilitySchedule/TimeLimit";
import { AuxRenderProps } from "../../LegacyDetailsList/cell-renderers";
import { ODataFixedList } from "../../Lists/ODataFixedList";
import RemediationContentUI from "../../RemediationIndicator/RemediationContentUI";
import InplaceSchedule from "../../Schedule/InplaceSchedule";
import SoftLinkCollection from "../../SoftLinkCollection";
import ReadonlyODataContainer from "../ReadonlyODataContainer";
import Tab from "../Tab";
import { formUtils } from "../utils";

const text = (annotation: IText, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { value } = addProps;

  return (
    <Text
      tabIndex={0}
      key={annotation.name || annotation.label}
      {...annotation}
      value={value || annotation.value}
      style={{ overflowWrap: annotation.formatter === "binary" ? "anywhere" : undefined }}
    />
  );
};

const label = (annotation: IText, addProps: IAdditionalFormFieldProps): JSX.Element => {
  let label = annotation.label;
  if (label && annotation.modelProperty && /\{/.test(label)) {
    label = bindingsUtils.resolveExpression({ [annotation.modelProperty]: addProps.value }, label);
  }

  return (
    <Label
      key={annotation.name || annotation.label}
      {...annotation}
      style={{ overflowWrap: annotation.formatter === "binary" ? "anywhere" : undefined }}
      label={label}
    />
  );
};

const imagepicker = (
  annotation: IImagePicker,
  addProps: IAdditionalFormFieldProps
): JSX.Element => {
  const { value, setValue } = addProps;

  let realValue: string | number | undefined = "";
  if (value === undefined) {
    realValue = annotation.value;
  } else {
    realValue = value;
  }

  return (
    <ImagePicker
      key={annotation.name}
      name={annotation.name}
      value={realValue as string}
      constraints={annotation.constraints}
      readOnly={annotation.readOnly}
      setValue={(imageSrc) => {
        // TODO: fix as any
        setValue(imageSrc, annotation as any);
      }}
      secondaryLabel={annotation.secondaryLabel}
      hidden={annotation.hidden}
    />
  );
};

const textfield = (annotation: ITextField, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { value, error, disabled, setValue, setError, intl, object } = addProps;
  const type = annotation.isConfidential ? "password" : annotation.type;
  let placeHolder = annotation.placeHolder;

  if (annotation.isConfidential && object) {
    const isPasswordSet = formUtils.isPasswordSet(annotation, object);

    placeHolder = isPasswordSet
      ? intl.formatMessage(commonMessages.passwordIsSet)
      : annotation.placeHolder;
  }

  let realValue: string | number | undefined = "";
  if (value === undefined) {
    realValue = annotation.value;
  } else {
    realValue = value;
  }

  const name = formUtils.getName(annotation);

  return (
    <TextField
      key={name}
      id={annotation.id || name}
      name={name}
      type={type}
      value={realValue}
      errorMessage={disabled ? undefined : error?.errorMessage}
      pattern={annotation.pattern}
      required={annotation.required}
      autoComplete={annotation.autocomplete}
      placeHolder={disabled ? undefined : placeHolder}
      disabled={annotation.disabled || disabled}
      multiline={annotation.multiline}
      autoFocus={annotation.autoFocus}
      nullValueDecorator={annotation.nullValueDecorator}
      tooltip={annotation.tooltip}
      tip={annotation.tip}
      hidden={annotation.hidden}
      suffix={annotation.suffix}
      prefix={annotation.prefix}
      copyEnabled={annotation.copyEnabled}
      hideWhenEmpty={annotation.hideWhenEmpty}
      hideRequiredAsterix={annotation.hideRequiredAsterix}
      isConfidential={annotation.isConfidential}
      autocomplete={annotation.autocomplete}
      renderInput={annotation.renderInput}
      renderEmptyLabel={annotation.renderEmptyLabel}
      styles={{ root: { width: "100%" } }}
      rows={annotation.rows || 0}
      setValue={(v: any) => {
        setValue(v, annotation);
        if (error) {
          formUtils.checkError(annotation, v as string, setError, intl);
        }
      }}
      onBlur={() => {
        formUtils.checkError(annotation, value, setError, intl);
      }}
      label={annotation.label}
      formatter={annotation.formatter}
      readOnly={annotation.readOnly}
      note={annotation.note}
      possibleValues={annotation.possibleValues}
      dataTestId={annotation.dataTestId}
      secondaryLabel={annotation.secondaryLabel}
      maskInput={annotation.maskInput}
      maskCharacter={annotation.maskCharacter}
    />
  );
};
const choicegroup = (
  annotation: IChoiceGroup,
  addProps: IAdditionalFormFieldProps
): JSX.Element => {
  const { value, disabled, setValue } = addProps;
  const name = formUtils.getName(annotation);

  return (
    <ChoiceGroup
      key={annotation.name}
      id={annotation.id || name}
      value={value}
      tooltip={annotation.tooltip}
      disabled={disabled}
      label={annotation.label}
      setValue={(v) => setValue(v, annotation)}
      hidden={annotation.hidden}
      readOnly={annotation.readOnly}
      visible={annotation?.visible}
      note={annotation.note}
      tip={annotation.tip}
      options={annotation.options}
      styles={{ root: { width: "100%" } }}
      dataTestId={annotation.dataTestId}
      secondaryLabel={annotation.secondaryLabel}
    />
  );
};

const checkbox = (annotation: ICheckbox, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { value, disabled, formId, setValue } = addProps;
  const name = formUtils.getName(annotation);

  return (
    <Checkbox
      key={name}
      id={`${annotation.id || name}_${formId}`}
      value={!!value}
      tooltip={annotation.tooltip}
      disabled={disabled}
      hidden={annotation.hidden}
      label={annotation.label || stringUtils.toSpaceDelimitedPascalCase(annotation.name!)}
      setValue={(v) => setValue(v, annotation)}
      readOnly={annotation.readOnly}
      visible={(annotation as ICheckbox)?.visible}
      note={annotation.note}
      tip={annotation.tip}
      dataTestId={annotation.dataTestId}
      secondaryLabel={annotation.secondaryLabel}
    />
  );
};
const toggle = (annotation: IToggle, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { value, disabled, setValue } = addProps;
  const name = formUtils.getName(annotation);

  return (
    <Toggle
      key={name}
      id={annotation.id || name}
      value={!!value}
      tooltip={annotation.tooltip}
      disabled={disabled}
      label={annotation.label}
      setValue={(v) => setValue(v, annotation)}
      readOnly={annotation.readOnly}
      visible={(annotation as ICheckbox)?.visible}
      note={annotation.note}
      tip={annotation.tip}
      onText={annotation.onText}
      offText={annotation.offText}
      dataTestId={annotation.dataTestId}
      secondaryLabel={annotation.secondaryLabel}
      hidden={annotation.hidden}
    />
  );
};

const dropdown = (annotation: IDropDown, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { disabled, setValue } = addProps;

  const hasBindings = annotation?.bindings ? !!Object.keys(annotation?.bindings)?.length : false;
  return (
    <DropDown
      key={annotation.name}
      disabled={hasBindings ? disabled : annotation.disabled}
      setValue={(v) => {
        setValue(v, annotation);
      }}
      // onBlur={(e) => setValue(e.currentTarget.value, annotation)}
      label={annotation.label}
      options={(annotation as IDropDown).options!}
      required={annotation.required}
      readOnly={annotation.readOnly}
      hidden={annotation.hidden}
      value={addProps.value}
      tooltip={annotation.tooltip}
      placeHolder={annotation.placeHolder}
      note={annotation.note}
      tip={annotation.tip}
      styles={{ root: { width: "100%" } }}
      renderInput={annotation.renderInput}
      renderEmptyLabel={annotation.renderEmptyLabel}
      dataTestId={annotation.dataTestId}
      secondaryLabel={annotation.secondaryLabel}
      skipAutoSelectFirstOption={annotation.skipAutoSelectFirstOption}
    />
  );
};

const combobox = (annotation: IComboBox, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { disabled, setValue, error, setError, intl } = addProps;

  const hasBindings = annotation?.bindings ? !!Object.keys(annotation?.bindings)?.length : false;
  return (
    <ComboBox
      key={annotation.name}
      disabled={hasBindings ? disabled : annotation.disabled}
      setValue={(v) => {
        setValue(v, annotation);

        if (error) {
          formUtils.checkError(annotation, v as string, setError, intl);
        }
      }}
      // onBlur={(e) => setValue(e.currentTarget.value, annotation)}
      label={annotation.label}
      options={(annotation as IDropDown).options!}
      required={annotation.required}
      readOnly={annotation.readOnly}
      value={addProps.value}
      tooltip={annotation.tooltip}
      placeHolder={annotation.placeHolder}
      note={annotation.note}
      tip={annotation.tip}
      allowFreeInput={annotation.allowFreeInput}
      renderInput={annotation.renderInput}
      // Temporary. Should be fixed in cayo.ui
      styles={{
        root: { width: "100%" },
        callout: { minWidth: 60 },
        container: { width: "100%" },
      }}
      dataTestId={annotation.dataTestId}
      secondaryLabel={annotation.secondaryLabel}
    />
  );
};

const mvText = (annotation: IMultiValue, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { value, disabled, setValue, error, setError, intl } = addProps;

  let realValue: string[] | number[] | undefined = [];
  if (value === undefined) {
    realValue = annotation.value;
  } else {
    realValue = value;
  }

  const name = formUtils.getName(annotation);

  const type = annotation!.type?.split(".")[1];

  if (annotation.variant === MultiValueVariants.Compact) {
    return (
      <InlineMultiValue
        id={annotation.id || name}
        key={name}
        name={name}
        disabled={annotation.disabled || disabled}
        required={annotation.required}
        readOnly={annotation.readOnly}
        label={annotation.label}
        tooltip={annotation.tooltip}
        placeHolder={disabled ? undefined : annotation.placeHolder}
        possibleValues={annotation.possibleValues}
        hidden={annotation.hidden}
        dataTestId={annotation.dataTestId}
        value={realValue}
        allowDuplicate={annotation.allowDuplicate}
        allowUserInput={annotation.allowUserInput}
        allowItemCopy={annotation.allowItemCopy}
        addButtonTitle={annotation.addButtonTitle}
        setValue={(v) => {
          setValue(v, annotation);
          if (error) {
            formUtils.checkError(annotation, v, setError, intl);
          }
        }}
        onBlur={() => {
          formUtils.checkError(annotation, value, setError, intl);
        }}
        error={error || annotation.error}
        onDuplicate={() => {
          setError(name!, { errorMessage: intl.formatMessage(commonMessages.duplicateValueError) });
        }}
      />
    );
  }

  return (
    <MultiValue
      id={annotation.id}
      key={name}
      name={name}
      type={type}
      value={realValue}
      readOnly={annotation?.readOnly}
      error={error || annotation.error}
      pattern={annotation.pattern}
      required={annotation.required}
      placeHolder={disabled ? undefined : annotation.placeHolder}
      disabled={annotation.disabled || disabled}
      autoFocus={annotation.autoFocus}
      addButtonTitle={annotation.addButtonTitle}
      setValue={(v: any) => {
        setValue(v, annotation);
        if (error) {
          formUtils.checkError(annotation, v, setError, intl);
        }
      }}
      onBlur={() => {
        formUtils.checkError(annotation, value, setError, intl);
      }}
      validateValue={(v: string) => {
        return !formUtils.checkError(annotation, v, setError, intl);
      }}
      label={annotation.label || stringUtils.toSpaceDelimitedPascalCase(annotation.name!)}
      tooltip={annotation.tooltip}
      formatter={annotation.formatter}
      nullValueDecorator={annotation.nullValueDecorator}
      allowItemCopy={annotation.allowItemCopy}
      dataTestId={annotation.dataTestId}
      secondaryLabel={annotation.secondaryLabel}
      hidden={annotation.hidden}
      possibleValues={annotation.possibleValues}
      allowDuplicate={annotation.allowDuplicate}
      allowUserInput={annotation.allowUserInput}
    />
  );
};

const mvCheckbox = (
  annotation: IMultiCheckbox,
  addProps: IAdditionalFormFieldProps
): JSX.Element => {
  const { value, disabled, setValue } = addProps;

  return (
    <MultiCheckbox
      key={annotation.name}
      setValue={(v) => {
        setValue(v, annotation);
      }}
      value={value}
      required={annotation.required}
      disabled={annotation.disabled || disabled}
      autoFocus={annotation.autoFocus}
      label={annotation.label || stringUtils.toSpaceDelimitedPascalCase(annotation.name!)}
      tooltip={annotation.tooltip}
      possibleValues={annotation.possibleValues}
      horizontal={annotation.horizontal}
      readOnly={annotation.readOnly}
      dataTestId={annotation.dataTestId}
      secondaryLabel={annotation.secondaryLabel}
      formatter={annotation.formatter}
    />
  );
};

const datetimebox = (
  annotation: IDateTimeBox,
  addProps: IAdditionalFormFieldProps
): JSX.Element => {
  const { value, disabled, setValue, setError } = addProps;

  return (
    <DateTimeBox
      key={annotation.name}
      {...annotation}
      setError={setError}
      setValue={(v: any) => {
        setValue(v, annotation);
      }} // TODO: remove IFormFieldAnnotation
      editMode={annotation.editMode}
      label={annotation.label!}
      value={value === undefined ? annotation.value : value}
      disabled={disabled}
      hidden={annotation.hidden}
      allowTextInput={annotation.allowTextInput}
      dataTestId={annotation.dataTestId}
      // eslint-disable-next-line  no-inline-styles/no-inline-styles
      style={{ minWidth: "200px" }}
    />
  );
};

const inplaceavailabilityschedule = (
  annotation: IInplaceAvailabilitySchedule,
  addProps: IAdditionalFormFieldProps
): JSX.Element => {
  const { object } = addProps;

  return <InplaceAvailabilitySchedule key={annotation.name} {...annotation} object={object} />;
};

const inplaceschedule = (
  annotation: IFormField,
  addProps: IAdditionalFormFieldProps
): JSX.Element => {
  const { object } = addProps;

  return <InplaceSchedule key={annotation.name} {...annotation} object={object} />;
};

const objectlink = (annotation: IFormField, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { object } = addProps;

  return <ObjectLink key={annotation.name} {...annotation} object={object} />;
};

const link = (annotation: ILink) => {
  return (
    <Link
      key={annotation.name}
      name={annotation.name!}
      href={annotation.href}
      title={annotation.title}
      dataTestId={annotation.dataTestId}
    />
  );
};

const objectdisplay = (annotation: IObjectDisplay, addProps: IAdditionalFormFieldProps) => {
  return (
    <ObjectDisplay
      name={annotation.name!}
      value={addProps.value || annotation.value}
      format={annotation.format}
      label={annotation.label}
    />
  );
};

const html = (annotation: IHtml, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { value } = addProps;

  return <Html key={annotation.name} {...annotation} value={annotation.value || value} />;
};

type multiValueComponentTypes = "mv.text" | "mv.checkbox";

const multiValueRenderers: Record<multiValueComponentTypes, any> = {
  "mv.checkbox": mvCheckbox,
  "mv.text": mvText,
};

const fixedlist = (annotation: IFixedList, addProps: IAdditionalFormFieldProps) => {
  return <ODataFixedList key={annotation.name} {...annotation} addProps={addProps} />;
};

const objectlinks = (annotation: IObjectLinks, addProps: IAdditionalFormFieldProps) => {
  return (
    <SoftLinkCollection key={annotation.name} {...annotation} addProps={addProps} style={{}} />
  );
};

const propertygroup = (
  annotation: ITextField & { items?: IUIItem[] },
  addProps: IAdditionalFormFieldProps,
  addFormProps?: IAdditionalFormProps
) => {
  const { items, name } = annotation;

  const isEmptyOdataObject = !items?.length && !addProps.value;
  if (isEmptyOdataObject) {
    return <Fragment />;
  }

  const isComplex = formUtils.isComplexItem(annotation);

  if (annotation?.readOnly) {
    annotation.items?.forEach((i: any) => (i.readOnly = !!annotation.readOnly));
  }

  return (
    <ExpandableContainer
      label={annotation.label!}
      tooltip={annotation.tooltip}
      border={isComplex ? "dashed" : undefined}
      style={!isComplex ? { margin: "16px 0 0 0" } : undefined}
      key={"group_" + name}
    >
      {!!items?.length && <Fragment>{renderForm(annotation, addFormProps!)}</Fragment>}
      {!items?.length && <ReadonlyODataContainer object={addProps.value} />}
    </ExpandableContainer>
  );
};

// use lazy loading otherwise the tests fail
const AgreementPage = React.lazy(async () => {
  const module = await import("cayo.ui.wizards");
  return { default: module.AgreementPage };
});

const eula = (annotation: IEula, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { value, setValue } = addProps;

  return (
    <React.Suspense fallback={<LoadingOverlay />}>
      <AgreementPage accepted={value} setAccepted={(v) => setValue(v, annotation)} framed />
      <AgreementBanner />
    </React.Suspense>
  );
};

const trialfeaturelist = (): JSX.Element => {
  return (
    <React.Suspense fallback={<LoadingOverlay />}>
      <TrialFeatureList />
    </React.Suspense>
  );
};

const activationplan = (annotation: IEula, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { value, setValue } = addProps;

  return <ActivationPlan value={value} setValue={(v) => setValue(v, annotation)} />;
};

const messagebar = (annotation: IMessageBar, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { messageBarType, text, ...other } = annotation;

  const officeMessageBarType: MessageBarTypeExtended = messageBarType || "info";
  return (
    <MessageBar
      key={annotation.name}
      {...other}
      text={text || addProps.value}
      messageBarType={officeMessageBarType}
    />
  );
};

const importfile = (annotation: IImportFile, addProps: IAdditionalFormFieldProps): JSX.Element => {
  const { value, setValue } = addProps;

  return (
    <ImportFile
      key={annotation.name}
      value={value}
      fileExt={annotation.fileExt}
      setValue={(v: any) => {
        setValue(v, annotation);
      }}
      label={annotation.label}
      formatter={annotation.formatter}
    />
  );
};

const timelimit = (
  annotation: IInplaceAvailabilitySchedule,
  value: any,
  // error: string | undefined,
  // disabled: boolean | undefined,
  //setValue: (value: any, annotation: IFormFieldAnnotation) => void,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setError: (error: string | undefined) => void,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  auxProps: AuxRenderProps
): JSX.Element => {
  const props = annotation;
  const { value: fieldValue, setValue } = value;

  return (
    <TimeLimit
      name={annotation.name!}
      value={fieldValue as unknown as AvailabilitySchedule[]}
      setValue={setValue}
      serverTimeZone={props.serverTimeZone}
      selectionMode={annotation.selectionMode}
      hidden={annotation.hidden}
    />
  );
};

const tab = (
  annotation: ITab,
  addProps: IAdditionalFormFieldProps,
  addFormProps?: IAdditionalFormProps
): JSX.Element => {
  return <Tab {...annotation} model={addProps?.object} addFormProps={addFormProps} />;
};

const remediationcomplexity = (
  annotation: { label: string },
  addProps: IAdditionalFormFieldProps
) => <RemediationContentUI text={annotation.label} level={addProps?.value} />;

const licensecontrol = (
  annotation: ILicenseLink,
  addProps: IAdditionalFormFieldProps,
  addFormProps: IAdditionalFormProps
) => {
  const { label } = annotation;
  const { object, setValue, intl } = addProps;
  const { model } = addFormProps;

  // if there are multiple license groups, they will be in the "value" prop, along with the "count" prop
  const licenseOptions = model.LicenseOptions || object.LicenseOptions || [];
  const hasValue = "value" in licenseOptions;

  return (
    <LicenseOptions
      label={label}
      viewState={annotation.viewState}
      licenseOptions={hasValue ? licenseOptions.value : licenseOptions}
      intl={intl}
      setValue={(licenses: INode[]) => {
        setValue(licenses, annotation);
      }}
      type={annotation.type}
    />
  );
};

const wysiwyghtmleditor = (annotation: ITextField, addProps: IAdditionalFormFieldProps) => {
  const { value, setValue } = addProps;
  const { hidden, readOnly } = annotation;

  return (
    <WysiwygHtmlEditor
      key={annotation.name}
      name={annotation.name!}
      label={annotation.label}
      value={value}
      readOnly={readOnly || false}
      hidden={hidden}
      dataTestId={annotation.dataTestId}
      setValue={(value) => {
        setValue(value, annotation);
      }}
    />
  );
};

const textrun = (annotation: ITextRun, addProps: IAdditionalFormFieldProps): JSX.Element => {
  return <TextRun key={annotation.name} lines={annotation.lines} model={addProps?.object} />;
};

const formRenderers = {
  licensecontrol,
  licenselinkcontrol: licensecontrol,
  remediationcomplexity,
  textfield,
  text,
  label,
  number: textfield,
  objectdisplay,
  checkbox,
  toggle,
  dropdown,
  combobox,
  datetimebox,
  inplaceschedule,
  inplaceavailabilityschedule,
  objectlink,
  link,
  html,
  ...multiValueRenderers,
  fixedlist,
  objectlinks,
  propertygroup,
  eula,
  trialfeaturelist,
  activationplan,
  choicegroup,
  importfile,
  messagebar,
  timelimit,
  tab,
  imagepicker,
  wysiwyghtmleditor,
  textrun,
};

export default formRenderers;

export type formComponentTypes = keyof typeof formRenderers;
